/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { createLinkIconStyles } from "@rc/App/SharedStyles/sharedStyles";
import { XngProductContextModel } from "@rc/Overrides/Widgets/ProductList/ProductListProductContext";
import { xScreen } from "@rc/XDL/Utilities";
import parse from "html-react-parser";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
    language: state.context.session.language,
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: LinkPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    sortOrderWrapper?: InjectableCss;
    lineInnerTopGridItem?: GridItemProps;
    selectRowCheckboxGridItem?: GridItemProps;
    lineInnerBottomGridItem?: GridItemProps;
    infoWrapper?: InjectableCss;
    canEditWishListWrapper?: InjectableCss;
    canEditWishListStylesWrapper?: InjectableCss;
    deleteLinkStyledWrapper?: InjectableCss;
    canEditWishListLeftSection?: InjectableCss;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        ...createLinkIconStyles(
            "Edit",
            {
                typographyProps: {
                    css: css`
                        ${xScreen("lte", "md")} {
                            margin: 10px 0;
                        }
                    `,
                },
            },
            false,
        ),
    },
    lineInnerContainer: {
        gap: 10,
    },
    lineInnerTopGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-top: 6px;
            }
        `,
    },
    lineInnerBottomGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-bottom: 6px;
            }
        `,
    },
    selectRowCheckboxGridItem: {
        width: 12,
        css: css`
            margin-bottom: 12px;
            ${xScreen("gt", "md")} {
                display: none;
            }
        `,
    },
    selectedCheckboxHidden: { below: "lg" },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 32px;
            display: flex;
        `,
    },
    imageWrapper: {
        css: css`
            width: 112px;
            height: 112px;
            min-width: 0;
            flex-direction: column;
            ${xScreen("lt", "sm")} {
                width: 70px;
                height: 70px;
            }
        `,
    },
    descriptionGridItem: {
        width: [12, 12, 12, 9.5, 9.5] as any,
        printWidth: 6,
    },
    infoWrapper: {
        css: css`
            margin-left: 22px;
            flex: 1;
            > div {
                padding-left: 0;
            }
            ${xScreen("lt", "sm")} {
                margin-left: 16px;
            }
        `,
    },
    canEditWishListWrapper: {
        css: css`
            width: calc(100% - 182px);
            ${xScreen("lte", "md")} {
                width: calc(100% - 134px);
            }
            ${xScreen("lt", "sm")} {
                width: 100%;
            }
        `,
    },
    canEditWishListStylesWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    deleteLinkStyledWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        `,
    },
    canEditWishListLeftSection: {
        css: css`
            width: 182px;
            ${xScreen("lte", "md")} {
                width: 134px;
            }
            ${xScreen("lt", "sm")} {
                display: none;
            }
        `,
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    notesStyles: {
        heading: {
            transform: "capitalize",
            css: css`
                font-size: 14px;
                line-height: 22px;
                font-weight: 500;
            `,
        },
        text: {
            css: css`
                font-size: 14px;
                line-height: 22px;
            `,
        },
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
            &&& {
                > div {
                    padding-left: 0;
                }
            }
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 12, 2.5, 2.5] as any,
        printWidth: 5,
        css: css`
            &&& {
                ${xScreen("lte", "md")} {
                    margin-left: 134px;
                }
                ${xScreen("lte", "xs")} {
                    margin-left: 0;
                }
            }
        `,
    },
    priceAndAvailabilityInnerContainer: {
        gap: 10,
        css: css`
            white-space: nowrap;
        `,
    },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: 12,
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        price: {
            priceText: {
                weight: 500,
                size: 20,
                lineHeight: "32px",
                color: "common.ui6",
            },
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [4, 2, 2, 4, 4],
        css: css`
            padding-top: 12px;
        `,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [8, 10, 10, 8, 8],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
            padding-right: 0;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        sizeVariant: "small",
        css: css`
            width: auto;
            margin-left: auto;
            ${xScreen("lte", "md")} {
                margin-right: auto;
                margin-left: initial;
            }
            ${xScreen("lt", "sm")} {
                margin-left: auto;
                margin-right: initial;
                width: 100%;
                margin-bottom: 20px;
            }
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        ...createLinkIconStyles(
            "Close",
            {
                icon: {
                    iconProps: {
                        size: 9,
                    },
                },
                css: css`
                    ${xScreen("lte", "md")} {
                        margin-left: auto;
                    }
                `,
            },
            false,
        ),
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
            ${xScreen("lte", "md")} {
                margin-left: auto;
            }
        `,
    },
    updateQuantityLink: {
        ...createLinkIconStyles("RefreshCw", undefined, false),
    },
    quantityUpdatedIcon: {
        ...createLinkIconStyles(
            "Check",
            {
                typographyProps: {
                    weight: 300,
                },
            },
            false,
        ),
    },
    quantityUpdatedText: {
        size: 12,
    },

    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
    sortOrderWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            position: absolute;
            bottom: 15px;
            ${xScreen("lte", "md")} {
                bottom: initial;
                right: 0;
            }
        `,
    },

    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    language,
}) => {
    const product = mapWishListLineToProduct(wishListLine);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;
    const createdOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishListLine.createdOn),
        language,
        options: { year: "2-digit", month: "numeric", day: "numeric" },
    });

    const { productSettings } = settingsCollection;
    const productContext: XngProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
        index: wishList.wishListLineCollection?.findIndex(line => line.id === wishListLine.id),
    };

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDiscontinued(wishListLine, productInfo)) {
        return (
            <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-restricted-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.lineInnerTopGridItem}></GridItem>
                <GridItem {...styles.restrictedDescriptionGridItem}
                    className="wishlist-restricted-line-description-grid-item"
                >
                    <Typography {...styles.restrictedDescriptionText}
                        className="wishlist-restricted-line-description-text"
                    >
                        {parse(wishListLine.shortDescription, parserOptions)}
                    </Typography>

                    {wishListLine.properties?.variant?.length > 0 && (
                        <Typography>
                            {translate("Weight")}: {wishListLine.properties.variant}
                        </Typography>
                    )}
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                    <Typography>
                        {translate("Added")}: {createdOnDisplay}
                    </Typography>
                </GridItem>
                <GridItem width={[12, 12, 4, 4, 5]}>
                    <Typography
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                    >
                        {isWishListLineRestricted(wishListLine, productInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                    </Typography>
                </GridItem>
                <GridItem width={[12, 12, 2, 2, 2]}>
                    {canEditWishList && (
                        <Link
                            {...styles.deleteLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            {translate("Remove Item")}
                        </Link>
                    )}
                </GridItem>
                <GridItem {...styles.lineInnerBottomGridItem}></GridItem>
            </GridContainer>
        );
    }


    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductContext.Provider value={productContext}>
            <GridContainer
                {...styles.lineInnerContainer}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.selectRowCheckboxGridItem}>
                    <Checkbox
                        {...styles.selectedCheckbox}
                        checked={isSelected}
                        onChange={selectChangeHandler}
                        data-test-selector="myListDetails_selectRowCheckbox"
                    />
                </GridItem>
                <GridItem {...styles.descriptionGridItem} className="wishlist-line-description-grid-item">
                    <Hidden {...styles.selectedCheckboxHidden}>
                        <Checkbox
                            {...styles.selectedCheckbox}
                            checked={isSelected}
                            onChange={selectChangeHandler}
                            hiddenIconText={translate("Select")}
                            data-test-selector="myListDetails_selectRowCheckbox"
                        />
                    </Hidden>
                    <StyledWrapper {...styles.imageWrapper}>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        {product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />

                        <GridItem {...styles.partNumbersGridItem} className="wishlist-restricted-line-part-number-grid-item">
                            {wishListLine.properties?.variant?.length > 0 && (
                                <Typography>
                                    {translate("Weight")}: {wishListLine.properties.variant}
                                </Typography>
                            )}
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                            <Typography>
                                {translate("Added")}: {createdOnDisplay}
                            </Typography>
                        </GridItem>
                        {wishListLine.notes && (
                            <GridItem {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                                <SmallHeadingAndText
                                    heading={translate("Notes")}
                                    text={wishListLine.notes}
                                    extendedStyles={styles.notesStyles}
                                />
                            </GridItem>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.priceAndAvailabilityGridItem}>
                    <GridContainer {...styles.priceAndAvailabilityInnerContainer}>
                        {wishList.isAutogenerated && (
                            <GridItem {...styles.purchasedDateGridItem}>
                                <Typography {...styles.purchasedDateLabelText}>
                                    {translate("Purchased Date")}
                                </Typography>
                                <Typography {...styles.purchasedDateText}>
                                    <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                </Typography>
                            </GridItem>
                        )}
                        <GridItem {...styles.priceGridItem}>
                            <ProductPrice
                                product={productContext}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                extendedStyles={styles.productPriceStyles}
                                showLabel={false}
                            />
                            <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                        </GridItem>
                        {productSettings.showInventoryAvailability && (
                            <GridItem {...styles.availabilityGridItem}>
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            </GridItem>
                        )}
                        <GridItem {...styles.uomAndQuantityGridItem}>
                            <GridContainer {...styles.uomAndQuantityInnerContainer}>
                                {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                    <GridItem {...styles.uomGridItem}>
                                        <ProductUnitOfMeasureSelect
                                            disabled={!canEditWishList}
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        />
                                    </GridItem>
                                )}
                                <GridItem {...styles.quantityGridItem}>
                                    <ProductQuantityOrdered extendedStyles={styles.productQuantityOrderedStyles} />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.buttonsGridItem}>
                            <ProductAddToCartButton
                                notes={wishListLine.notes}
                                extendedStyles={styles.addToCartButton}
                                data-test-selector="addToCart"
                                {...{ wishlistName: wishList.name }}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <StyledWrapper {...styles.canEditWishListLeftSection}></StyledWrapper>
                <StyledWrapper {...styles.canEditWishListWrapper}>
                    {canEditWishList && (
                        <StyledWrapper {...styles.canEditWishListStylesWrapper}>
                            <Link
                                {...styles.editNotesLink}
                                className="wishlist-line-edit-notes-link"
                                onClick={() => onEditNotesClick(wishListLine)}
                                data-test-selector="myListDetails_addOrEditNote"
                            >
                                {translate("Add / Edit Notes")}
                            </Link>
                            <StyledWrapper {...styles.deleteLinkStyledWrapper}>
                                <Link
                                    {...styles.deleteLink}
                                    onClick={() => onDeleteClick(wishListLine)}
                                    data-test-selector="deleteListItem"
                                >
                                    {translate("Remove Item ")}
                                </Link>
                                {((productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered) ||
                                    (isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered)) && (
                                        <StyledWrapper {...styles.updateQuantityWrapper}
                                            className="wishlist-line-update-quantity-wrapper"
                                        >
                                            {productInfo.qtyOrdered > 0 &&
                                                wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                                    <Link
                                                        {...styles.updateQuantityLink}
                                                        className="wishlist-line-update-quantity-link"
                                                        onClick={() => onUpdateQuantityClick()}
                                                    >
                                                        {translate("Update QTY")}
                                                    </Link>
                                                )}
                                            {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                                <Link {...styles.quantityUpdatedIcon}
                                                    className="wishlist-line-quantity-updated-icon"
                                                >{translate("QTY updated")}</Link>
                                            )}
                                        </StyledWrapper>
                                    )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </GridContainer>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
